"use client";
import type { GridState } from "../../types/GridStates";
import { getGridStateLabel } from "../../types/GridStates";
import { Badge, type BadgeProps } from "../Badge";
import { Icon, type IconName } from "../Icon";
import { Skeleton } from "../Skeleton";

/**
 * The arrow for a state that has a direction.
 *
 * Only two of the four do. `idle` is neither importing nor exporting and
 * `unknown` is missing telemetry, so neither has an arrow to draw — and drawing
 * the fallback one would assert an import that is not happening. A partial record
 * rather than a ternary so adding a state cannot silently inherit a direction.
 */
const DIRECTION_ICON: Partial<Record<GridState, IconName>> = {
  importing: "ArrowDown",
  exporting: "ArrowUp",
};

export interface GridStateBadgeProps extends Omit<BadgeProps, "variant" | "children"> {
  /** The grid state to display */
  state: GridState;
  /** Custom label override (defaults to auto-capitalized state name) */
  label?: string;
  /** Show only directional icon without text (arrow up for exporting, arrow down for importing) */
  showIcon?: boolean;
  /** Loading state */
  isLoading?: boolean;
  /** Fixed width for column alignment (useful in tables) */
  fixedWidth?: boolean;
}

/**
 * GridStateBadge
 *
 * A specialized badge component for displaying grid states (importing, exporting,
 * idle, unknown). Optionally shows directional icons to indicate power flow
 * direction — only `importing` and `exporting` have one, and the other two render
 * their label instead. Uses semantic grid state colors from the theme.
 *
 * @example
 * ```tsx
 * <GridStateBadge state="importing" />
 * <GridStateBadge state="exporting" showIcon />
 * <GridStateBadge state="importing" label="Grid Import" />
 * ```
 */
export function GridStateBadge({
  state,
  label,
  showIcon = false,
  isLoading,
  size = "sm",
  shape = "pill",
  fixedWidth,
  className,
  ...badgeProps
}: GridStateBadgeProps) {
  if (isLoading) {
    return <Skeleton className="inline-flex h-6 w-24" />;
  }

  const displayLabel = label ?? getGridStateLabel(state);
  const icon = DIRECTION_ICON[state];
  const iconSize = size === "sm" ? 12 : size === "md" ? 14 : 16;

  // When showIcon is true, show only the icon without text (compact mode).
  // A directionless state falls through to the label instead: an icon-only badge
  // for `idle` would have to pick an arrow, and both arrows would be wrong.
  if (showIcon && icon) {
    return (
      <Badge
        variant={state}
        size={size}
        shape={shape}
        className={`w-6 justify-center ${className || ""}`}
        {...badgeProps}
      >
        <Icon name={icon} size={iconSize} />
      </Badge>
    );
  }

  // Default: show label with optional icon
  return (
    <Badge
      variant={state}
      size={size}
      shape={shape}
      className={fixedWidth ? `w-28 justify-center ${className || ""}` : className}
      {...badgeProps}
    >
      {displayLabel}
    </Badge>
  );
}
